import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

// Map of file types to mime types
const mimeTypes = {
  jpg: "image/jpeg",
  png: "image/png",
  tiff: "image/tiff",
  pdf: "application/pdf",
};

// Allowed file types
export type FileType = "jpg" | "png" | "tiff" | "pdf";
// Data format to read the file
export type FileReadFormat = "dataUrl" | "arrayBuffer" | "text";

@Injectable({
  providedIn: "root",
})
export class FileService {
  constructor() {}

  /**
   * Open file dialog and get selected file(s).
   * 
   * @param multiple Whether to read single or multiple files
   * @param fileTypes Allowed file types
   * @returns Observable to resolve read file objects
   */
  public selectFile(
    multiple?: boolean,
    fileTypes?: FileType[]
  ): Observable<File[]> {
    return new Observable((subsriber) => {
      const inputEl = document.createElement("input");
      inputEl.type = "file";
      inputEl.className = "hidden-file-input";
      inputEl.multiple = multiple;
      inputEl.accept = this.getFileAcceptAttr(fileTypes);
      document.body.appendChild(inputEl);

      inputEl.addEventListener("change", () => {
        subsriber.next(Array.from(inputEl.files));
        subsriber.complete();
        inputEl.remove();
      });

      inputEl.click();
    });
  }

  /**
   * Read file content
   * 
   * @param file File to read
   * @param format Data format to read
   * @returns Observable to resolve file content.
   */
  public readFileContent(
    file: File,
    format: FileReadFormat = "text"
  ): Observable<any> {
    return new Observable((subsriber) => {
      const reader = new FileReader();
      reader.addEventListener("load", (event) => {
        subsriber.next(event.target.result);
        subsriber.complete();
      });

      switch (format) {
        case "arrayBuffer":
          reader.readAsArrayBuffer(file);
          break;
        case "dataUrl":
          reader.readAsDataURL(file);
          break;
        case "text":
          reader.readAsText(file);
          break;
      }
    });
  }

  /**
   * Download file data by creating link tag
   * 
   * @param filename File name of the download file
   * @param data File content data
   */
  public downloadPDF(filename: string, data: string) {
    const element = document.createElement('a');

    element.setAttribute('href', data);
    element.setAttribute('download', `${filename}.pdf`);
    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }

  /**
   * Get accept attribute string based on fileTypes
   * 
   * @param fileTypes File types to read
   * @returns Generated accept attribute string
   */
  private getFileAcceptAttr(fileTypes?: FileType[]) {
    if (!fileTypes) {
      return Object.values(mimeTypes).join(",");
    }

    return fileTypes.map((type) => mimeTypes[type]).join(",");
  }
}
